import { useState, useCallback, useRef } from "react";
import { testimonials } from "@/data/feedbackData";
import { socialLinks } from "@/data/sampleData";
import { allPhotos } from "@/data/photos";
import { ChevronLeft, ChevronRight } from "lucide-react";

const GRID_SIZE = 5; // 1 big + 4 small

const featured = testimonials.slice(0, 3);

export const CommunitySection = () => {
  const [page, setPage] = useState(0);
  const [activeCaption, setActiveCaption] = useState<string | null>(null);
  const totalPages = Math.ceil(allPhotos.length / GRID_SIZE);

  // Touch/swipe state
  const touchStartX = useRef<number | null>(null);

  const getGridPhotos = useCallback(() => {
    const start = page * GRID_SIZE;
    // Wrap around if we don't have a full page
    return Array.from({ length: GRID_SIZE }, (_, i) => allPhotos[(start + i) % allPhotos.length]);
  }, [page]);

  const prev = () => setPage((p) => (p - 1 + totalPages) % totalPages);
  const next = () => setPage((p) => (p + 1) % totalPages);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) {
      if (delta < 0) next();
      else prev();
    }
    touchStartX.current = null;
  };

  const handleCardTap = (key: string) => {
    setActiveCaption((prev) => (prev === key ? null : key));
  };

  const gridPhotos = getGridPhotos();

  /** Build alt text from location + date */
  const getAlt = (photo: typeof gridPhotos[0]) =>
    photo.alt || `${photo.location}${photo.date ? `, ${photo.date}` : ""}`;

  return (
    <section className="py-16 md:py-24 bg-muted/40">
      <div className="container mx-auto px-5 md:px-8">
        {/* Section header */}
        <div className="flex items-end justify-between mb-8">
          <div className="max-w-2xl">
            <div className="text-xs font-bold uppercase tracking-widest text-brand-orange mb-2">
              Community
            </div>
            <h2 className="text-2xl md:text-4xl font-black tracking-tight text-foreground">
              Real people.<br className="hidden sm:block" />
              <span className="text-muted-foreground">Real fun.</span>
            </h2>
          </div>

          {/* Navigation arrows — hidden on mobile, visible on md+ */}
          <div className="hidden md:flex items-center gap-2">
            <button
              type="button"
              onClick={prev}
              className="w-9 h-9 rounded-full border border-border bg-background hover:bg-muted flex items-center justify-center text-foreground transition-colors"
              aria-label="Previous photos"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={next}
              className="w-9 h-9 rounded-full border border-border bg-background hover:bg-muted flex items-center justify-center text-foreground transition-colors"
              aria-label="Next photos"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* ── Photo grid: 1 big + 4 small ── */}
        <div
          className="grid grid-cols-2 md:grid-cols-4 auto-rows-[180px] md:auto-rows-[220px] gap-2.5 md:gap-3"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {/* Big photo — spans 2 cols and 2 rows */}
          {(() => {
            const photo = gridPhotos[0];
            const cardKey = `big-${page}`;
            const isActive = activeCaption === cardKey;
            return (
              <div
                className="col-span-2 row-span-2 relative rounded-xl overflow-hidden shadow-md group/card bg-muted cursor-pointer"
                role="button"
                tabIndex={0}
                aria-label={`${getAlt(photo)} — tap to see details`}
                onClick={() => handleCardTap(cardKey)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleCardTap(cardKey); } }}
              >
                <img
                  src={photo.src}
                  loading="lazy"
                  alt={getAlt(photo)}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover/card:scale-105"
                  style={{ objectPosition: photo.objectPosition || 'center' }}
                />
                {/* Location badge for non-London photos */}
                {photo.location !== "London" && (
                  <div className="absolute top-2 right-2 z-10 px-2 py-1 rounded-full bg-black/50 backdrop-blur-sm text-white text-[10px] font-semibold">
                    <span aria-hidden="true">📍</span> {photo.location}
                  </div>
                )}
                <div className={`absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent transition-opacity duration-300 opacity-0 ${isActive ? "opacity-100" : ""} md:group-hover/card:opacity-100`} />
                <div className={`absolute bottom-0 left-0 right-0 p-3 md:p-4 transition-all duration-300 translate-y-2 opacity-0 ${isActive ? "translate-y-0 !opacity-100" : ""} md:group-hover/card:translate-y-0 md:group-hover/card:opacity-100`}>
                  <div className="text-white text-sm font-semibold leading-tight">{photo.location}</div>
                  {photo.date && <div className="text-white/60 text-xs mt-0.5">{photo.date}</div>}
                </div>
              </div>
            );
          })()}

          {/* 4 smaller photos */}
          {gridPhotos.slice(1).map((photo, i) => {
            const cardKey = `${page}-${i}`;
            const isActive = activeCaption === cardKey;
            return (
              <div
                key={cardKey}
                className="relative rounded-xl overflow-hidden shadow-md group/card bg-muted cursor-pointer"
                role="button"
                tabIndex={0}
                aria-label={`${getAlt(photo)} — tap to see details`}
                onClick={() => handleCardTap(cardKey)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleCardTap(cardKey); } }}
              >
                <img
                  src={photo.src}
                  alt={getAlt(photo)}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover/card:scale-105"
                  style={{ objectPosition: photo.objectPosition || 'center' }}
                />
                {/* Location badge for non-London photos */}
                {photo.location !== "London" && (
                  <div className="absolute top-2 right-2 z-10 px-2 py-1 rounded-full bg-black/50 backdrop-blur-sm text-white text-[10px] font-semibold">
                    <span aria-hidden="true">📍</span> {photo.location}
                  </div>
                )}
                <div className={`absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent transition-opacity duration-300 opacity-0 ${isActive ? "opacity-100" : ""} md:group-hover/card:opacity-100`} />
                <div className={`absolute bottom-0 left-0 right-0 p-3 transition-all duration-300 translate-y-2 opacity-0 ${isActive ? "translate-y-0 !opacity-100" : ""} md:group-hover/card:translate-y-0 md:group-hover/card:opacity-100`}>
                  <div className="text-white text-sm font-semibold leading-tight">{photo.location}</div>
                  {photo.date && <div className="text-white/60 text-xs mt-0.5">{photo.date}</div>}
                </div>
              </div>
            );
          })}
        </div>

        {/* Page dots */}
        <div className="mt-4 flex justify-center gap-1.5">
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              type="button"
              key={i}
              onClick={() => setPage(i)}
              className={`w-2.5 h-2.5 rounded-full transition-colors cursor-pointer ${i === page ? "bg-brand-orange" : "bg-muted-foreground/20 hover:bg-muted-foreground/40"}`}
              aria-label={`Photo page ${i + 1}`}
            />
          ))}
        </div>

        {/* Testimonials */}
        <div className="mt-12 grid md:grid-cols-3 gap-4">
          {featured.map((t) => (
            <div key={t.id} className="rounded-xl border border-border bg-card p-5 flex flex-col">
              <p className="text-sm leading-relaxed text-foreground italic flex-1">
                &ldquo;{t.quote}&rdquo;
              </p>
              <div className="mt-4 flex items-center gap-3">
                {t.avatar ? (
                  <img
                    src={t.avatar}
                    alt={t.name}
                    loading="lazy"
                    className="w-10 h-10 rounded-full object-cover flex-shrink-0 border-2 border-brand-orange/20"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-primary flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                    {t.name.charAt(0)}
                  </div>
                )}
                <div className="text-xs font-bold text-foreground">{t.name}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Social links bar */}
        <div className="mt-8 flex flex-wrap items-center gap-3 justify-center">
          <span className="text-xs text-muted-foreground font-medium mr-1">Join the community:</span>
          <a href={socialLinks.strava} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-orange-50 dark:bg-orange-950/30 text-orange-700 dark:text-orange-400 text-xs font-bold border border-orange-200/50 dark:border-orange-800/30 hover:bg-orange-100 dark:hover:bg-orange-950/50 transition-colors">
            <span aria-hidden="true" style={{ display: "inline-block", transform: "scaleX(-1)" }}>🏃</span> Strava Club
          </a>
          <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-pink-50 dark:bg-pink-950/30 text-pink-700 dark:text-pink-400 text-xs font-bold border border-pink-200/50 dark:border-pink-800/30 hover:bg-pink-100 dark:hover:bg-pink-950/50 transition-colors">
            📷 Instagram
          </a>
          <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300 text-xs font-bold border border-blue-200/50 dark:border-blue-800/30 hover:bg-blue-100 dark:hover:bg-blue-950/50 transition-colors">
            👥 Facebook
          </a>
          <a href={socialLinks.telegram} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-blue-50 dark:bg-blue-950/30 text-blue-600 dark:text-blue-400 text-xs font-bold border border-blue-200/50 dark:border-blue-800/30 hover:bg-blue-100 dark:hover:bg-blue-950/50 transition-colors">
            💬 Telegram (RU)
          </a>
        </div>
      </div>
    </section>
  );
};
